import { AppBar, Box, Button, Dialog, DialogContent, Typography } from '@mui/material'
import { SurveyCreator, SurveyCreatorComponent } from 'survey-creator-react'
import { useThemeContext } from '@common/theme'
import { darkTheme } from '@common/theme/surveyJSCustomDarkTheme'
import { DefaultDark } from 'survey-creator-core/themes'

import 'survey-core/defaultV2.min.css'
import 'survey-creator-core/survey-creator-core.min.css'
import { FormBuilderJsonResponse, FormDefinitionPayload } from '@study_setup/types/form_definition'
import { useEffect, useState } from 'react'
import { Serializer } from 'survey-core'

// Global serializer changes only need to be registered once per app lifetime.
Serializer.addProperty('text', {
  name: 'defaultValue',
  category: 'general',
  visible: true,
})

Serializer.addProperty('dropdown', {
  name: 'defaultValue',
  category: 'general',
  visible: true,
})

const creatorOptions = {
  showLogicTab: true,
  isAutoSave: true,
  questionTypes: ['text', 'comment', 'checkbox', 'radiogroup', 'dropdown', 'html'],
  showSidebar: false,
  toolboxLocation: 'left',
  allowEditExpressionsInTextEditor: true,
  showHtmlInDesigner: true,
}

export type FormBuilderDialogProps = {
  onClose: () => void
  header: string
  onSave: (data: FormBuilderJsonResponse) => void
  data?: FormDefinitionPayload | null
}

export default function FormBuilderDialog({ onClose, header, onSave, data }: FormBuilderDialogProps) {
  const { themeMode } = useThemeContext()

  // Create the SurveyCreator instance once per dialog mount so unrelated re-renders
  // (e.g. auth token refresh causing context updates) don't wipe out in-progress edits.
  const [creator] = useState(() => new SurveyCreator(creatorOptions))

  useEffect(() => {
    creator.onShowingProperty.add((_, options) => {
      const type = options.obj.getType()

      if (type === 'panel' || type === 'survey') {
        options.canShow = options.property.name === 'title'
      } else {
        options.canShow = true
      }
    })
  }, [creator])

  useEffect(() => {
    if (themeMode === 'dark') {
      // DefaultDark cssVariables include numeric deltas; applyCreatorTheme types them as string-only
      creator.applyCreatorTheme(DefaultDark as unknown as Parameters<SurveyCreator['applyCreatorTheme']>[0])
      creator.preferredColorPalette = 'dark'
      creator.survey?.applyTheme?.(darkTheme)
    } else {
      creator.preferredColorPalette = 'light'
    }
  }, [creator, themeMode])

  const handleSave = () => {
    const surveyData = creator.getSurveyJSON()
    onSave(surveyData)
  }

  useEffect(() => {
    if (!data) {
      creator.JSON = {}
      return
    }

    const processedSurveyJson = (() => {
      if (!data.fields || !Array.isArray(data.fields) || data.fields.length === 0) {
        return {
          title: data.name || 'Untitled Form',
          pages: [
            {
              title: data.name || 'Untitled Form',
              elements: [],
            },
          ],
        }
      }

      const pageSeparators = data.fields.filter((field) => field.isPageSeparator)

      if (data.metadata?.originalPages) {
        const originalPages = data.metadata.originalPages
        const pages = []
        let fieldIndex = 0

        for (const pageInfo of originalPages) {
          const pageElements = []
          let elementsToAdd = pageInfo.elementCount

          while (elementsToAdd > 0 && fieldIndex < data.fields.length) {
            const field = data.fields[fieldIndex]

            if (field.isPageSeparator) {
              fieldIndex++
              continue
            }

            pageElements.push(field)
            fieldIndex++
            elementsToAdd--
          }

          pages.push({
            title: pageInfo.title || data.name || 'Untitled Form',
            name: pageInfo.name,
            description: pageInfo.description,
            elements: pageElements,
          })

          if (fieldIndex < data.fields.length && data.fields[fieldIndex].isPageSeparator) {
            fieldIndex++
          }
        }

        return {
          title: data.name || 'Untitled Form',
          pages: pages,
        }
      } else if (pageSeparators.length > 0) {
        const pages = []
        let currentPageElements = []
        let currentPageTitle = data.name || 'Untitled Form'

        for (const field of data.fields) {
          if (field.isPageSeparator) {
            if (currentPageElements.length > 0) {
              pages.push({
                name: `page${pages.length + 1}`,
                title: currentPageTitle,
                elements: [...currentPageElements],
              })
            }

            currentPageElements = []
            if (field.html && typeof field.html === 'string') {
              const match = field.html.match(/data-page-title="([^"]*)"/)
              currentPageTitle = match && match[1] ? match[1] : data.name || 'Untitled Form'
            } else {
              currentPageTitle = data.name || 'Untitled Form'
            }
          } else {
            currentPageElements.push(field)
          }
        }

        if (currentPageElements.length > 0) {
          pages.push({
            name: `page${pages.length + 1}`,
            title: currentPageTitle,
            elements: [...currentPageElements],
          })
        }

        return {
          title: data.name || 'Untitled Form',
          pages: pages,
        }
      } else {
        return {
          title: data.name || 'Untitled Form',
          pages: [
            {
              title: data.name || 'Untitled Form',
              elements: data.fields,
            },
          ],
        }
      }
    })()

    creator.JSON = processedSurveyJson
  }, [data, creator])

  return (
    <Dialog fullScreen open={true} onClose={() => null} aria-labelledby="form-builder-dialog-title">
      <AppBar sx={{ position: 'relative', bgcolor: 'paper.main' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
          <Typography id="form-builder-dialog-title" variant="subtitle1" color="text.primary" ml={1}>
            {header}
          </Typography>
          <Box display="flex">
            <Button variant="contained" sx={{ mr: 1 }} onClick={handleSave} aria-label="Save form">
              Save
            </Button>
            <Button variant="text" onClick={onClose} aria-label="Close form builder">
              Close
            </Button>
          </Box>
        </Box>
      </AppBar>
      <DialogContent sx={{ p: 0 }}>
        <SurveyCreatorComponent creator={creator} aria-label="Form builder interface" />
      </DialogContent>
    </Dialog>
  )
}
